import React, { useEffect, useState } from 'react';
import {
  addMacroWatchlistIndex,
  previewMacroWatchlistIndex,
  resetMacroWatchlist,
  getDecisionApiError
} from '../../../services/decisionApi';

const SECTIONS = [
  { id: 'market', label: '市场概览' },
  { id: 'benchmark', label: '基准权益' },
  { id: 'custom', label: '自定义' }
];

function emptyForm() {
  return { name: '', secidOrCode: '', section: 'custom', strip: false };
}

export default function MacroWatchlistModal({ open, onClose, onChanged }) {
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setForm(emptyForm());
    setPreview(null);
    setError('');
    setMessage('');
    setSaving(false);

    function onKeyDown(e) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setPreview(null);
    setError('');
    setMessage('');
  }

  async function handlePreview() {
    setError('');
    setMessage('');
    setPreview(null);
    try {
      const data = await previewMacroWatchlistIndex({
        name: form.name.trim() || undefined,
        secid_or_code: form.secidOrCode.trim(),
        section: form.section,
        strip: form.strip
      });
      setPreview(data.preview);
      if (!form.name.trim() && data.preview?.name) {
        setForm((prev) => ({ ...prev, name: data.preview.name }));
      }
      if (!data.preview?.quote_ok) {
        setError('东方财富暂无法拉取该代码，请检查格式或换一个代码');
      }
    } catch (err) {
      setError(getDecisionApiError(err, '预览失败'));
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.secidOrCode.trim()) {
      setError('请填写指数代码');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await addMacroWatchlistIndex({
        name: form.name.trim() || undefined,
        secid_or_code: form.secidOrCode.trim(),
        section: form.section,
        strip: form.strip
      });
      setMessage('指数已添加');
      onChanged?.();
      setTimeout(() => {
        onClose?.();
      }, 400);
    } catch (err) {
      setError(getDecisionApiError(err, '添加失败'));
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!window.confirm('恢复默认指数列表？将覆盖当前配置。')) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await resetMacroWatchlist();
      setMessage('已恢复默认列表');
      onChanged?.();
    } catch (err) {
      setError(getDecisionApiError(err, '恢复失败'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="macro-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="decision-card macro-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="macro-watchlist-title"
      >
        <div className="macro-modal-head">
          <h3 id="macro-watchlist-title">添加实时指数</h3>
          <button type="button" className="macro-modal-close" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </div>

        <p className="decision-muted macro-modal-sub">
          自动从东方财富拉取行情。支持 000698、1.000698、000698.SH；同花顺科创类如
          {' '}
          <strong>1B0698.SH</strong>
          {' '}
          会自动转为 1.000698（科创100）。
        </p>

        <form onSubmit={handleAdd}>
          <div className="macro-watchlist-form">
            <label>
              <div className="decision-muted">指数名称</div>
              <input
                className="decision-input"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="可留空，预览时自动填充"
              />
            </label>
            <label>
              <div className="decision-muted">指数代码</div>
              <input
                className="decision-input"
                value={form.secidOrCode}
                onChange={(e) => updateField('secidOrCode', e.target.value)}
                placeholder="如 1B0698.SH / 000698 / 1.000698"
                required
              />
            </label>
            <label>
              <div className="decision-muted">展示分组</div>
              <select
                className="decision-select"
                value={form.section}
                onChange={(e) => updateField('section', e.target.value)}
              >
                {SECTIONS.map((item) => (
                  <option key={item.id} value={item.id}>{item.label}</option>
                ))}
              </select>
            </label>
            <label className="macro-watchlist-check">
              <input
                type="checkbox"
                checked={form.strip}
                onChange={(e) => updateField('strip', e.target.checked)}
              />
              <span>顶栏摘要显示</span>
            </label>
          </div>

          {preview ? (
            <div className="macro-watchlist-preview decision-muted">
              预览：{preview.name} · 东方财富 {preview.secid}
              {preview.quote?.change_pct != null ? ` · ${preview.quote.change_pct}%` : ' · 暂无行情'}
            </div>
          ) : null}

          {message ? <p className="decision-muted macro-modal-msg">{message}</p> : null}
          {error ? <p className="decision-error macro-modal-msg">{error}</p> : null}

          <div className="macro-watchlist-actions">
            <button
              type="button"
              className="decision-btn ghost"
              onClick={handlePreview}
              disabled={!form.secidOrCode.trim() || saving}
            >
              预览行情
            </button>
            <button type="submit" className="decision-btn" disabled={saving || !form.secidOrCode.trim()}>
              {saving ? '保存中…' : '确认添加'}
            </button>
            <button type="button" className="decision-btn ghost" onClick={handleReset} disabled={saving}>
              恢复默认
            </button>
            <button type="button" className="decision-btn ghost" onClick={onClose} disabled={saving}>
              取消
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
